import { HttpError, type IHttpClient, type QueryValue, type RequestOptions } from "@crialume/sdk";

export interface FetchHttpClientOptions {
  baseUrl: string;
  /** Lido a cada requisição — nunca cacheado, nunca lido direto do storage aqui. */
  getAccessToken: () => string | null;
  /** Chamado quando uma requisição autenticada recebe 401 (e a renovação não resolveu). */
  onUnauthorized?: (url: string) => void;
  /**
   * Tenta renovar a sessão após um 401. `true` = token novo disponível: a
   * requisição é repetida UMA vez. Sem a opção, o 401 vai direto para `onUnauthorized`.
   */
  refreshSession?: () => Promise<boolean>;
  /**
   * Chamado quando uma requisição autenticada recebe 403. As permissões podem ter mudado no
   * servidor depois da emissão do token (ex.: role alterada): quem escuta renova a sessão.
   */
  onForbidden?: (url: string) => void;
  /**
   * Envio de cookies. Padrão `"include"`: o refresh token vive em cookie
   * HttpOnly e precisa acompanhar as chamadas de autenticação, mesmo com a API
   * em outra origem (o backend libera com `AllowCredentials`).
   */
  credentials?: RequestCredentials;
  timeoutMs?: number;
  defaultHeaders?: Record<string, string>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Cliente HTTP único da aplicação (sem Axios). Centraliza o que no CadFrn
 * estava espalhado entre `AxiosHttpClient`, `lib-adapters/api.ts` e leituras
 * diretas de `localStorage` em vários pontos.
 */
export class FetchHttpClient implements IHttpClient {
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: FetchHttpClientOptions) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get<T>(url: string, options?: RequestOptions) {
    return this.request<T>("GET", url, undefined, options);
  }
  post<T>(url: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>("POST", url, body, options);
  }
  put<T>(url: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>("PUT", url, body, options);
  }
  patch<T>(url: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>("PATCH", url, body, options);
  }
  delete<T>(url: string, options?: RequestOptions) {
    return this.request<T>("DELETE", url, undefined, options);
  }

  withBaseUrl(baseUrl: string): IHttpClient {
    return new FetchHttpClient({ ...this.options, baseUrl });
  }

  private async request<T>(
    method: string,
    url: string,
    body: unknown,
    options: RequestOptions = {},
    retried = false,
  ): Promise<T> {
    const fullUrl = buildUrl(this.options.baseUrl, url, options.params);

    const headers = new Headers({ Accept: "application/json", ...this.options.defaultHeaders });
    const isRawBody =
      body instanceof FormData || body instanceof Blob || body instanceof URLSearchParams;
    if (body !== undefined && !isRawBody) headers.set("Content-Type", "application/json");
    for (const [key, value] of Object.entries(options.headers ?? {})) headers.set(key, value);

    if (!options.anonymous) {
      const token = this.options.getAccessToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
    }

    const timeoutMs = options.timeoutMs ?? this.options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const signal = options.signal
      ? AbortSignal.any([options.signal, timeoutSignal])
      : timeoutSignal;

    let response: Response;
    try {
      response = await this.fetchImpl(fullUrl, {
        method,
        headers,
        signal,
        credentials: this.options.credentials ?? "include",
        body:
          body === undefined ? undefined : isRawBody ? (body as BodyInit) : JSON.stringify(body),
      });
    } catch (error) {
      // Cancelamento explícito do chamador (ex.: React Query) propaga como está.
      if (options.signal?.aborted) throw error;
      const message = timeoutSignal.aborted
        ? `Tempo limite de ${timeoutMs}ms excedido`
        : "Falha de rede";
      throw new HttpError(message, 0, fullUrl);
    }

    const payload =
      response.ok && options.responseType === "blob"
        ? await response.blob()
        : await parseBody(response);

    if (!response.ok) {
      if (response.status === 401 && !options.anonymous) {
        // Token vencido no meio do caminho: renova e repete uma vez.
        if (!retried && this.options.refreshSession && (await this.options.refreshSession())) {
          return this.request<T>(method, url, body, options, true);
        }
        this.options.onUnauthorized?.(fullUrl);
      }
      if (response.status === 403 && !options.anonymous) {
        this.options.onForbidden?.(fullUrl);
      }
      throw new HttpError(
        extractErrorMessage(payload) ?? `HTTP ${response.status}`,
        response.status,
        fullUrl,
        payload,
      );
    }

    options.onResponse?.({ status: response.status, headers: response.headers });
    return payload as T;
  }
}

function buildUrl(
  baseUrl: string,
  url: string,
  params?: Record<string, QueryValue | QueryValue[]>,
): string {
  const isAbsolute = /^https?:\/\//i.test(url);
  const joined = isAbsolute ? url : `${baseUrl.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;

  if (!params) return joined;

  const search = new URLSearchParams();
  for (const [key, raw] of Object.entries(params)) {
    for (const value of Array.isArray(raw) ? raw : [raw]) {
      if (value !== null && value !== undefined) search.append(key, String(value));
    }
  }
  const query = search.toString();
  if (!query) return joined;
  return `${joined}${joined.includes("?") ? "&" : "?"}${query}`;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return undefined;
  const isJson = response.headers.get("content-type")?.includes("json") ?? false;
  if (!isJson) return text;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * Extrai a mensagem dos formatos de erro mais comuns de API:
 * `{ message }`, `{ error: "..." }`, `{ error: ["..."] }`, `{ errors: [...] }`,
 * ProblemDetails (`{ title, detail }`).
 */
export function extractErrorMessage(payload: unknown): string | undefined {
  if (typeof payload === "string") return payload.trim() || undefined;
  if (!payload || typeof payload !== "object") return undefined;

  const record = payload as Record<string, unknown>;
  for (const key of ["message", "detail", "error", "errors", "title"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (Array.isArray(value)) {
      const first = value.find((item): item is string => typeof item === "string" && !!item.trim());
      if (first) return first.trim();
    }
  }
  return undefined;
}
